import { useMemo, useState, useCallback } from "react";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRegisterPageShortcuts } from "@/hooks/usePageShortcuts";
import {
  type FeedItem,
  scopeFilter,
  syncSubscription,
  deleteSubscription,
} from "@/lib/feedReader";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { AddFeedDialog } from "@/components/feed-reader/AddFeedDialog";
import { tierFeedSubscriptionLimit, tierFeedExtractionDailyCap } from "@/lib/tier";

import { FeedItemReader } from "@/components/feed-reader/FeedItemReader";
import { ReadingFontToggle } from "@/components/reading/ReadingFontToggle";
import { Rss, Plus } from "lucide-react";
import { toast } from "sonner";
import { AppSeo } from "@/components/AppSeo";
import {
  readPaneSizes,
  persistPaneSizes,
} from "@/components/feed-reader/feedReaderHelpers";
import { FeedsListPane } from "@/components/feed-reader/FeedsListPane";
import { FeedItemsListPane } from "@/components/feed-reader/FeedItemsListPane";
import { useFeedReaderShortcuts } from "@/components/feed-reader/useFeedReaderShortcuts";
import { useFeedItemActions } from "@/components/feed-reader/useFeedItemActions";
import { useRef } from "react";
import { useFeedReaderData } from "@/components/feed-reader/useFeedReaderData";

const MyFeedReader = () => {
  const { active, workspaces, libraryMode, loading: wsLoading } = useWorkspace();
  const isMobile = useIsMobile();
  const scopeIds = useMemo(
    () => scopeFilter(libraryMode, active, workspaces),
    [libraryMode, active, workspaces],
  );
  const showChips = libraryMode === "shared" && workspaces.length > 1;
  const wsLabel = useCallback(
    (id: string) => {
      const w = workspaces.find((x) => x.id === id);
      return w?.workspace_label || w?.display_name || "Workspace";
    },
    [workspaces],
  );

  const data = useFeedReaderData({
    scopeIds,
    wsLoading,
    activeWorkspaceId: active?.id ?? null,
  });
  const {
    subscriptions, items, setItems, unreadCounts, setUnreadCounts,
    filter, setFilter, selectedSub, setSelectedSub,
    loading, selectedItemId, setSelectedItemId,
    searchInput, setSearchInput, searchQuery,
    extractionUsed, setExtractionUsed, refreshExtractionUsage,
    itemRowRefs, refresh, subById, visibleItems, selectedItem,
  } = data;

  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [mobileReaderOpen, setMobileReaderOpen] = useState(false);
  const [paneSizes] = useState<[number, number, number]>(readPaneSizes);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const handleSync = async (id: string) => {
    setSyncingId(id);
    try {
      const r = await syncSubscription(id);
      toast.success(r.not_modified ? "Already up to date" : `${r.new_items} new item${r.new_items === 1 ? "" : "s"}`);
      await refresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSyncingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSubscription(id);
      toast.success("Feed removed");
      if (selectedSub === id) setSelectedSub(null);
      await refresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const {
    actingItemId,
    markingAllRead,
    optimisticUpdate,
    handleStar,
    handleArchive,
    handleToggleRead,
    handleOpenExternal,
    handleMarkAllRead,
    handlePromote,
    handleDeleteItem,
  } = useFeedItemActions({
    items,
    setItems,
    setUnreadCounts,
    filter,
    selectedItemId,
    setSelectedItemId,
    refresh,
  });

  const selectItem = useCallback(
    (id: string) => {
      setSelectedItemId(id);
      if (isMobile) setMobileReaderOpen(true);
    },
    [isMobile, setSelectedItemId],
  );

  // Shortcut help registry.
  useRegisterPageShortcuts("/feed-reader", [
    {
      heading: "Feed Reader",
      items: [
        { keys: ["j", "↓"], label: "Next item" },
        { keys: ["k", "↑"], label: "Previous item" },
        { keys: ["o", "Enter"], label: "Open in new tab" },
        { keys: ["s"], label: "Star / unstar" },
        { keys: ["e"], label: "Archive / unarchive" },
        { keys: ["r"], label: "Mark read / unread" },
        { keys: ["g", "u"], label: "Go to Unread" },
        { keys: ["g", "s"], label: "Go to Starred" },
        { keys: ["g", "a"], label: "Go to Archive" },
        { keys: ["g", "g"], label: "Go to All" },
        { keys: ["/"], label: "Focus search" },
      ],
    },
  ]);

  useFeedReaderShortcuts({
    visibleItems,
    selectedItemId,
    selectItem,
    setFilter,
    setSearchInput,
    searchInputRef,
    onOpenExternal: handleOpenExternal,
    onStar: handleStar,
    onArchive: handleArchive,
    onToggleRead: handleToggleRead,
  });

  const unreadShownCount = visibleItems.filter((it) => !it.read_at && !it.archived_at).length;
  const selectedSubObj = selectedItem ? subById.get(selectedItem.subscription_id) ?? null : null;

  // Per-workspace feed cap is keyed off the ACTIVE workspace's tier.
  const activeFeedCap = tierFeedSubscriptionLimit(active?.account_tier ?? null);
  const activeFeedCount = active
    ? subscriptions.filter((s) => s.workspace_id === active.id).length
    : 0;
  const atFeedCap = activeFeedCount >= activeFeedCap;
  const extractionCap = tierFeedExtractionDailyCap(active?.account_tier ?? null);

  // ---- Sub-renders -------------------------------------------------------

  const FeedsPane = (
    <FeedsListPane
      subscriptions={subscriptions}
      unreadCounts={unreadCounts}
      selectedSub={selectedSub}
      setSelectedSub={setSelectedSub}
      loading={loading}
      syncingId={syncingId}
      onSync={handleSync}
      onMarkAllRead={(id) => { setSelectedSub(id); void handleMarkAllRead(id); }}
      onDelete={handleDelete}
      showChips={showChips}
      wsLabel={wsLabel}
      active={active}
      activeFeedCount={activeFeedCount}
      activeFeedCap={activeFeedCap}
      extractionUsed={extractionUsed}
      extractionCap={extractionCap}
    />
  );

  const ItemsPane = (
    <FeedItemsListPane
      filter={filter}
      setFilter={setFilter}
      searchInput={searchInput}
      setSearchInput={setSearchInput}
      searchInputRef={searchInputRef}
      searchQuery={searchQuery}
      loading={loading}
      visibleItems={visibleItems}
      subscriptions={subscriptions}
      subById={subById}
      selectedItemId={selectedItemId}
      itemRowRefs={itemRowRefs}
      showChips={showChips}
      wsLabel={wsLabel}
      selectedSub={selectedSub}
      unreadShownCount={unreadShownCount}
      markingAllRead={markingAllRead}
      selectItem={selectItem}
      onMarkAllRead={handleMarkAllRead}
      onStar={handleStar}
      onToggleRead={handleToggleRead}
      onArchive={handleArchive}
      onDeleteItem={handleDeleteItem}
      onOpenExternal={handleOpenExternal}
    />
  );

  const ReaderPane = (
    <FeedItemReader
      item={selectedItem}
      subscription={selectedSubObj}
      workspaceLabel={selectedItem ? wsLabel(selectedItem.workspace_id) : null}
      showWorkspaceChip={showChips}
      onStar={handleStar}
      onArchive={handleArchive}
      onToggleRead={handleToggleRead}
      onPromote={handlePromote}
      isPromoting={selectedItem ? actingItemId === selectedItem.id : false}
      onExtractionComplete={(itemId, patch) => {
        optimisticUpdate(itemId, patch as Partial<FeedItem>);
        if (patch.extraction_status === "ok") {
          setExtractionUsed((n) => n + 1);
          window.setTimeout(() => { void refreshExtractionUsage(); }, 1500);
        }
      }}
      extractionUsed={extractionUsed}
      extractionCap={extractionCap}
    />
  );

  return (
    <>
    <AppSeo title="My Feed Reader" />
    <div className="flex h-full w-full flex-col">
      <header className="shrink-0 border-b border-border/60 bg-background px-6 py-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Rss className="h-5 w-5 text-primary" />
            My Feed Reader
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
            Subscribe to blogs, journals, and news feeds. New items show up here once a day &mdash; save anything worth keeping to your My Library.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ReadingFontToggle />
          <Button
            onClick={() => setAddOpen(true)}
            disabled={!active}
            size="sm"
            title={atFeedCap ? `At cap (${activeFeedCap}). Remove a feed or upgrade.` : undefined}
          >
            <Plus className="mr-2 h-4 w-4" /> Add feed
          </Button>
        </div>
      </header>

      {active && subscriptions.length === 0 ? (
        <div className="flex-1 min-h-0 flex items-center justify-center p-6">
          <div className="max-w-md text-center space-y-4">
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Rss className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">No feeds yet</h2>
              <p className="text-sm text-muted-foreground">
                Subscribe to a blog, journal, or news feed and new items will show up here once a day. Save anything worth keeping to your My Library.
              </p>
            </div>
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Add your first feed
            </Button>
          </div>
        </div>
      ) : isMobile ? (
        <div className="flex-1 min-h-0 grid grid-rows-[auto_1fr]">
          <details className="border-b border-border/60">
            <summary className="cursor-pointer select-none px-4 py-2 text-sm font-medium">
              Feeds ({subscriptions.length})
            </summary>
            <div className="max-h-72 overflow-auto">{FeedsPane}</div>
          </details>
          <div className="min-h-0">{ItemsPane}</div>
          <Sheet open={mobileReaderOpen} onOpenChange={setMobileReaderOpen}>
            <SheetContent side="right" className="w-full sm:max-w-xl p-0">
              {ReaderPane}
            </SheetContent>
          </Sheet>
        </div>
      ) : (
        <ResizablePanelGroup
          direction="horizontal"
          className="flex-1 min-h-0"
          onLayout={persistPaneSizes}
        >
          <ResizablePanel defaultSize={paneSizes[0]} minSize={14}>
            {FeedsPane}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={paneSizes[1]} minSize={28}>
            {ItemsPane}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={paneSizes[2]} minSize={30}>
            {ReaderPane}
          </ResizablePanel>
        </ResizablePanelGroup>
      )}

      <AddFeedDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        workspaceId={active?.id ?? null}
        onAdded={refresh}
        currentCount={activeFeedCount}
        cap={activeFeedCap}
      />
    </div>
    </>
  );
};

export default MyFeedReader;
